<template>
    <StandardLayout>
        <div class="admin-dashboard">
            <div class="cards-row">
                <div class="card" v-if="!token">
                    <h3 class="card-title">Import CSV</h3>
                    <form @submit.prevent="saveForm" class="import-form">
                        <div class="form-row">
                            <StationDropdown @select="onStation" :stations="stations" />
                        </div>
                        <label class="file-input-label">
                            <input type="file" @change="upload" class="file-input" />
                            <span class="file-input-trigger">{{ $t("admin.dashboard.chooseFile") }}</span>
                            <span class="file-input-name">{{ form.file ? form.file.name : $t("admin.dashboard.noFileChosen") }}</span>
                        </label>
                        <button class="button-solid" type="submit" :disabled="busy || !form.file || !form.stationId">Import</button>
                    </form>
                    <div v-if="busy">
                        Busy
                        <Spinner />
                    </div>
                </div>
                <div class="card" v-if="status">
                    <div class="status">
                        <div v-if="status.createdAt">Created</div>
                        <div v-if="status.startedAt">Started</div>
                        <div v-if="status.preparedAt">Prepared</div>
                        <div v-if="status.completedAt">Completed. Station data will refresh shortly.</div>
                    </div>
                    <div class="buttons">
                        <div>
                            <button class="action-btn" @click="onExplore()">
                                {{ $t("admin.stations.exploreData") }}
                            </button>
                        </div>
                        <div>
                            <button class="action-btn" @click="onImportAnother()">Import Another</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </StandardLayout>
</template>
<script lang="ts">
import Vue from "vue";
import StandardLayout from "../StandardLayout.vue";
import CommonComponents from "@/views/shared";
import { mapState } from "vuex";
import * as ActionTypes from "@/store/actions";
import { GlobalState } from "@/store/modules/global";
import { StationRef, ImportStarted, ImportStatus } from "@/api/api";
import { BookmarkFactory, serializeBookmark } from "@/views/viz/viz";
import StationDropdown from "@/views/admin/StationDropdown.vue";
import _ from "lodash";

export default Vue.extend({
    components: {
        StandardLayout,
        StationDropdown,
        ...CommonComponents,
    },
    props: {
        stationId: {
            type: Number,
            required: false,
        },
        token: {
            type: String,
            required: false,
        },
    },
    data(): {
        form: { stationId: number | null; file: any };
        started: ImportStarted | null;
        status: ImportStatus | null;
        busy: boolean;
        timer: any;
    } {
        return {
            form: { stationId: this.stationId, file: null },
            started: null,
            status: null,
            busy: false,
            timer: null,
        };
    },
    computed: {
        ...mapState({
            stations: (s: GlobalState) => Object.values(s.stations.user.stations),
        }),
    },
    mounted(): Promise<any> {
        if (this.token) {
            return this.query();
        } else {
            return this.$store.dispatch(ActionTypes.NEED_STATIONS);
        }
    },
    beforeDestroy() {
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = null;
        }
    },
    methods: {
        onStation(station: StationRef): void {
            this.form.stationId = station.id;
        },
        query(this: any): Promise<any> {
            if (!this.token) {
                return Promise.resolve(false);
            }
            if (!this.timer) {
                this.timer = setInterval(this.query, 1000);
            }
            return this.$services.api.importStatus(this.token).then((status) => {
                console.log("import:status", status);
                this.status = status;
                if (status.completedAt != null) {
                    clearInterval(this.timer);
                    this.timer = null;
                }
            });
        },
        upload(this: any, ev: any): void {
            const file = ev.target.files[0];
            this.form.file = file;
        },
        saveForm(): Promise<any> {
            if (!this.form.stationId) {
                return Promise.resolve(false);
            }
            this.busy = true;
            return this.$services.api
                .importUploadFile(this.form.stationId, this.form.file)
                .then((started) => {
                    console.log("import:started", started);
                    this.started = started;

                    return this.$router
                        .push({
                            name: "importDataStatus",
                            params: { token: started.token },
                        })
                        .then(() => {
                            return this.query();
                        });
                })
                .finally(() => {
                    this.busy = false;
                });
        },
        onImportAnother(): Promise<any> {
            this.status = null;
            this.form = {
                stationId: this.form.stationId,
                file: null,
            };

            return this.$router.push({
                name: "importData",
            });
        },
        onExplore(): void {
            if (this.status && this.status.stationId) {
                const bm = BookmarkFactory.forStation(this.status.stationId);
                const route = this.$router.resolve({ name: "exploreBookmark", query: { bookmark: serializeBookmark(bm) } });
                window.open(route.href, "_blank");
            }
        },
    },
});
</script>
<style scoped lang="scss">
@use "src/scss/variables";
@use "src/scss/mixins";

.section-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    margin-top: 30px;

    @include mixins.bp-down(variables.$md) {
        grid-template-columns: 1fr;
    }
}

.section-card {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 22px 24px;
    background: #fff;
    border: 1px solid var(--color-border);
    border-radius: 3px;
    text-decoration: none;
    color: var(--color-dark);
    transition: box-shadow 0.15s ease, transform 0.15s ease;

    &:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
        transform: translateY(-2px);
    }

    h3 {
        font-family: var(--font-family-bold);
        font-size: 16px;
        margin: 0 0 4px;
    }

    p {
        font-size: 13px;
        color: #6a6d71;
        margin: 0;
        line-height: 1.4;
    }
}

.card {
    background: #fff;
    border: 1px solid var(--color-border);
    border-radius: 3px;
    padding: 24px;
}

.import-form {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.file-input {
    display: none;
}

.file-input-label {
    display: flex;
    align-items: center;
    gap: 12px;
    cursor: pointer;
}

.file-input-trigger {
    display: inline-block;
    padding: 8px 16px;
    font-size: 13px;
    font-family: var(--font-family-medium);
    background: #f4f5f7;
    border: 1px solid var(--color-border);
    border-radius: 3px;
    white-space: nowrap;
    transition: background 0.15s ease;

    &:hover {
        background: #ecedf0;
    }
}

.file-input-name {
    font-size: 13px;
    color: #6a6d71;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.button-solid {
    align-self: flex-start;
    padding: 10px 24px;
    font-size: 14px;
    font-family: var(--font-family-bold);
    background-color: var(--color-secondary);
    color: #fff;
    border: none;
    border-radius: 3px;
    cursor: pointer;
    transition: opacity 0.15s ease;
    width: 200px;
    margin-top: 10px;

    &:hover {
        opacity: 0.9;
    }

    &:disabled {
        background: var(--color-border);
        cursor: not-allowed;
    }
}

.action-btn {
    padding: 5px 14px;
    font-size: 12px;
    font-family: var(--font-family-medium);
    color: var(--color-primary);
    background: transparent;
    border: 1px solid var(--color-primary);
    border-radius: 3px;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;
    white-space: nowrap;

    &:hover {
        background: var(--color-primary);
        color: #fff;
    }

    &:disabled {
        color: var(--color-border);
        border-color: var(--color-border);
        background: transparent;
        cursor: not-allowed;
    }
}

.card-row {
    display: flex;
    align-items: center;
    padding: 10px 12px;
    font-size: 14px;
    color: var(--color-dark);
}

.card .status {
    text-align: left;
}

.status {
    display: flex;
    flex-direction: column;
    align-items: start;

    * {
        font-weight: bold;
    }
}

.buttons {
    margin-top: 1em;
    display: flex;

    * {
        margin-right: 0.5em;
    }
}

.form-row {
    display: flex;
}

form .station-picker {
    width: 300px;
}
</style>
